import { PrayerAuthoringSession, prayerEditorIsCurrent } from './prayer-authoring';
import { getLocalContentEntity, listPrayerAttempts } from './content-lifecycle/store';
import type { ContentLifecycleAuthExpectation } from './content-lifecycle/store';
import type { PrayerAttemptV1, PrayerIntent, PrayerLocalCommitResult } from '@/types/prayer-command';
import type { CompletePrayerDocument } from '@/types/content-lifecycle';
import type { RecoveredAudioCommitClaim } from '@/types/audio-recovery';
import { mergeRecoveredVoiceText } from './audio-recovery';
import { holdUpdate } from './update-safety';
import { getSupabase } from './supabase';
import { useAdiaStore } from './store';

export interface UpdateDraftSnapshot {
  note: string;
  revision: number;
  loading: 'loading' | 'ready' | 'failed' | 'multiple';
  resolution: 'none' | 'saved' | 'unknown' | 'changed';
  operation: 'save' | 'discard' | null;
  saveError: boolean;
  saveNeedsReopen: boolean;
  discardError: boolean;
  savedWithNewWords: boolean;
}

type Words = { text: string; queueId?: string; fromCapture: boolean };
type Settlement = {
  auth: ContentLifecycleAuthExpectation;
  reviewed: PrayerAttemptV1;
  executed: PrayerAttemptV1;
  result: Extract<PrayerLocalCommitResult, { kind: 'committed' }> | null;
};
const drafts = new Map<string, PrayerUpdateDraft>();
const keyFor = (auth: ContentLifecycleAuthExpectation, prayerId: string) =>
  `${auth.ownerId}:${auth.authEpoch}:${prayerId}`;
const sameAttempt = (a: PrayerAttemptV1 | null, b: PrayerAttemptV1) => JSON.stringify(a) === JSON.stringify(b);
let stopWatching: (() => void) | null = null;

function retireStaleRecords(ownerId?: string | null) {
  for (const [key, draft] of drafts) {
    if ((ownerId !== undefined && draft.auth.ownerId !== ownerId) || !prayerEditorIsCurrent(draft.auth)) {
      draft.dispose(); drafts.delete(key);
    }
  }
  if (!drafts.size) { stopWatching?.(); stopWatching = null; }
}
function watchRecords() {
  if (stopWatching) return;
  const unsubscribe = useAdiaStore.subscribe((state, previous) => {
    if (state.prayers === previous.prayers && state.hydration === previous.hydration) return;
    retireStaleRecords();
    for (const draft of drafts.values()) void draft.refreshAvailability();
  });
  const subscription = getSupabase()?.auth.onAuthStateChange((_event, session) => {
    // Invalidate on the event's owner immediately, including a rapid A-B-A.
    // The microtask then sees the epoch installed by all existing callbacks.
    retireStaleRecords(session?.user.id ?? null);
    queueMicrotask(() => retireStaleRecords());
  }).data.subscription;
  stopWatching = () => { unsubscribe(); subscription?.unsubscribe(); };
}

/** Current-document NEW-update state only. No durable format/write authority.
 * Existing authoring transactions own attempts; this record owns its reload hold. */
export class PrayerUpdateDraft {
  readonly auth: ContentLifecycleAuthExpectation;
  readonly prayerId: string;
  session: PrayerAuthoringSession;
  entry: { id: string; date: string; note: string } | null = null;
  readonly receivedIds = new Set<string>();
  readonly claimedIds = new Set<string>();
  readonly excludedIds = new Set<string>();
  recoveryText = '';
  private pendingWords: Words[] = [];
  private listeners = new Set<() => void>();
  private loadPromise: Promise<void> | null = null;
  private snapshot: UpdateDraftSnapshot;
  private initialized = false;
  private binding: { attempt: PrayerAttemptV1; textRevision: number } | null = null;
  private pendingSettlement: Settlement | null = null;
  private releaseHold: (() => void) | null = null;
  private disposed = false;
  private available = true;
  private availabilityRead = 0;

  constructor(auth: ContentLifecycleAuthExpectation, prayerId: string, initialNote: string) {
    this.auth = auth; this.prayerId = prayerId;
    this.session = new PrayerAuthoringSession(auth);
    this.snapshot = { note: initialNote, revision: 0, loading: 'loading', resolution: 'none', operation: null,
      saveError: false, saveNeedsReopen: false, discardError: false, savedWithNewWords: false };
  }
  readonly getSnapshot = () => this.snapshot;
  readonly subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };
  isCurrent() {
    return !this.disposed && prayerEditorIsCurrent(this.auth) && drafts.get(keyFor(this.auth, this.prayerId)) === this;
  }
  private syncHold() {
    const needed = this.isCurrent() && (this.snapshot.operation !== null ||
      (this.available && (this.snapshot.note !== '' || this.pendingWords.length > 0)));
    if (needed && !this.releaseHold) this.releaseHold = holdUpdate('prayer-update-draft');
    else if (!needed && this.releaseHold) { const release = this.releaseHold; this.releaseHold = null; release(); }
  }
  dispose() { this.disposed = true; this.syncHold(); }
  /** The existing prayer projection is the invalidation signal. Confirm absence
   * against the owner's lifecycle row; transient loading never retires a draft.
   * Keep page memory dormant for Undo, without holding updates for a deleted
   * prayer indefinitely. No attempt, recording, tombstone or Undo ticket changes. */
  async refreshAvailability() {
    if (!this.isCurrent()) return;
    const generation = ++this.availabilityRead;
    if (useAdiaStore.getState().prayers.some((p) => p.id === this.prayerId)) {
      this.available = true; this.syncHold();
    }
    try {
      const entity = await getLocalContentEntity(this.auth.ownerId, 'prayer', this.prayerId);
      if (!this.isCurrent() || generation !== this.availabilityRead) return;
      const state = useAdiaStore.getState();
      if (entity) this.available = Boolean(entity.visibleDocument && !entity.pendingDeleteAt && !entity.heldReason && !entity.quarantineReason);
      else if (state.hydration.state === 'ready') this.available = state.prayers.some((p) => p.id === this.prayerId);
      this.syncHold();
    } catch { /* Unknown availability keeps the existing hold; no inferred deletion. */ }
  }
  patch(patch: Partial<UpdateDraftSnapshot>) {
    if (!this.isCurrent()) return;
    this.snapshot = { ...this.snapshot, ...patch }; this.syncHold();
    for (const listener of this.listeners) listener();
  }
  setNote(value: string) {
    if (!this.isCurrent() || this.snapshot.loading !== 'ready' || this.snapshot.operation) return;
    this.patch({ note: value, revision: this.snapshot.revision + 1,
      saveError: false, saveNeedsReopen: false, discardError: false, savedWithNewWords: false });
  }
  /** Save may fail before revising its previous attempt. Only the exact
   * submitted payload can bind this text revision; otherwise keep the older
   * binding, so resolving that older attempt cannot clear newer words. */
  rememberSubmittedAttempt(session: PrayerAuthoringSession, textRevision: number,
    intent: Extract<PrayerIntent, { kind: 'append' }>, claims: readonly RecoveredAudioCommitClaim[]) {
    const attempt = session.attempt;
    if (!this.isCurrent() || this.session !== session || !attempt ||
        attempt.ownerId !== this.auth.ownerId || attempt.prayerId !== this.prayerId ||
        JSON.stringify(attempt.intent) !== JSON.stringify(intent) ||
        JSON.stringify(attempt.recoveredAudio) !== JSON.stringify(claims)) return;
    this.binding = { attempt, textRevision };
  }
  load(refresh = false) {
    if (this.loadPromise || (!refresh && this.snapshot.loading === 'ready') || !this.isCurrent()) return this.loadPromise;
    this.patch({ loading: 'loading' });
    this.loadPromise = (async () => {
      const [attempts, entity] = await Promise.all([
        listPrayerAttempts(this.auth.ownerId), getLocalContentEntity(this.auth.ownerId, 'prayer', this.prayerId),
      ]);
      if (!this.isCurrent()) return;
      const matching = attempts.filter((attempt) => attempt.prayerId === this.prayerId && attempt.intent.kind === 'append');
      const previous = this.session.attempt;
      if (previous) {
        const current = matching.find((attempt) => attempt.commandId === previous.commandId);
        if (current && !sameAttempt(previous, current)) this.patch({ resolution: 'changed' });
        else if (!current) {
          // Only exact operation AND append identity prove consumption. An
          // absent attempt or another entry with equal words proves nothing.
          const document = entity?.visibleDocument as CompletePrayerDocument | null | undefined;
          const entry = previous.intent.kind === 'append' ? previous.intent.entry : null;
          const opMatches = entity?.pending?.opId === previous.commandId || entity?.installedEnvelope?.opId === previous.commandId;
          const saved = entry && document?.progressHistory.some((row) => row.id === entry.id && row.date === entry.date && row.note === entry.note);
          if (opMatches && saved && entity && !entity.pendingDeleteAt && !entity.heldReason && !entity.quarantineReason) {
            // Same reconciliation path as the proven Settings result. It never
            // creates an operation or infers a successor from matching text.
            this.settleResolved(previous, true, []);
          } else this.patch({ resolution: 'unknown' });
        } else this.patch({ resolution: 'none' });
      } else if (matching.length) {
        if (matching.length > 1 || this.initialized) this.patch({ loading: 'multiple' });
        else {
          const attempt = matching[0];
          if (attempt.intent.kind === 'append') {
            this.session.attempt = attempt; this.entry = attempt.intent.entry;
            this.patch({ note: attempt.intent.entry.note, revision: this.snapshot.revision + 1 });
            this.binding = { attempt, textRevision: this.snapshot.revision };
            for (const claim of attempt.recoveredAudio) { this.receivedIds.add(claim.queueItemId); this.claimedIds.add(claim.queueItemId); }
          }
        }
      }
      this.initialized = true;
      if (this.snapshot.loading !== 'multiple') this.patch({ loading: 'ready' });
      this.flushWords();
    })().catch(() => { this.patch({ loading: 'failed' }); }).finally(() => { this.loadPromise = null; });
    return this.loadPromise;
  }
  receive(text: string, queueId?: string, fromCapture = false) {
    if (!this.isCurrent() || !text.trim()) return;
    if (queueId && (this.receivedIds.has(queueId) || this.excludedIds.has(queueId))) return;
    if (queueId && this.pendingWords.some((words) => words.queueId === queueId)) return;
    const words = { text, queueId, fromCapture };
    if (this.snapshot.operation || this.snapshot.loading !== 'ready') {
      this.pendingWords.push(words); this.syncHold(); return;
    }
    this.applyWords(words);
  }
  private applyWords(words: Words) {
    if (words.queueId) {
      if (this.receivedIds.has(words.queueId) || this.excludedIds.has(words.queueId)) return;
      this.receivedIds.add(words.queueId); this.claimedIds.add(words.queueId);
    }
    if (words.fromCapture) this.recoveryText = mergeRecoveredVoiceText(this.recoveryText, words.text);
    this.patch({ note: mergeRecoveredVoiceText(this.snapshot.note, words.text), revision: this.snapshot.revision + 1 });
  }
  private flushWords() {
    if (!this.isCurrent() || this.snapshot.loading !== 'ready' || this.snapshot.operation) return;
    // Keep the buffer visible to syncHold until every word has been delivered.
    while (this.pendingWords.length) { this.applyWords(this.pendingWords[0]); this.pendingWords.shift(); }
    this.syncHold();
  }
  getRecoveryText() {
    return this.pendingWords.filter((words) => words.fromCapture)
      .reduce((text, words) => mergeRecoveredVoiceText(text, words.text), this.recoveryText);
  }
  begin(operation: 'save' | 'discard') {
    if (!this.isCurrent() || this.snapshot.loading !== 'ready' || this.snapshot.operation) return null;
    const captured = { revision: this.snapshot.revision, note: this.snapshot.note,
      session: this.session, entry: this.entry, claimedIds: new Set(this.claimedIds) };
    this.patch({ operation, saveError: false, saveNeedsReopen: false, discardError: false, savedWithNewWords: false });
    return captured;
  }
  private resetSession(excludedIds: Iterable<string>) {
    for (const id of excludedIds) this.excludedIds.add(id);
    this.claimedIds.clear(); this.entry = null; this.binding = null;
    this.session = new PrayerAuthoringSession(this.auth); this.recoveryText = '';
  }
  finish(revision: number, clear: boolean, excludedIds: Iterable<string> = []) {
    if (!this.isCurrent()) return;
    if (clear && revision === this.snapshot.revision) {
      this.resetSession(excludedIds);
      this.patch({ note: '', revision: revision + 1, resolution: 'none' });
    }
    this.patch({ operation: null });
    const settlement = this.pendingSettlement; this.pendingSettlement = null;
    if (settlement) this.settle(settlement);
    this.flushWords();
  }
  /** Non-durable, exact-version observation of an already successful Settings
   * action. Newer local words are not the version that Settings reviewed. */
  settle(settlement: Settlement) {
    if (!this.isCurrent() || settlement.auth.ownerId !== this.auth.ownerId || settlement.auth.authEpoch !== this.auth.authEpoch ||
        !this.binding || !sameAttempt(this.binding.attempt, settlement.reviewed)) return;
    if (this.snapshot.operation) { this.pendingSettlement = settlement; return; }
    const { executed, result } = settlement;
    if (executed.intent.kind !== 'append' || executed.commandId !== settlement.reviewed.commandId || executed.prayerId !== this.prayerId) return;
    if (result && (result.ownerId !== this.auth.ownerId || result.authEpoch !== this.auth.authEpoch ||
      result.opId !== executed.commandId || result.prayer.id !== this.prayerId ||
      !result.prayer.progressHistory.some((entry) => executed.intent.kind === 'append' && entry.id === executed.intent.entry.id && entry.date === executed.intent.entry.date && entry.note === executed.intent.entry.note))) return;
    this.settleResolved(settlement.reviewed, result !== null, result?.consumedAudio.map((claim) => claim.queueItemId) ?? []);
  }
  private settleResolved(previous: PrayerAttemptV1, saved: boolean, consumedIds: string[]) {
    if (!this.binding || !sameAttempt(this.binding.attempt, previous)) { this.patch({ resolution: 'unknown' }); return; }
    const unchanged = this.snapshot.revision === this.binding.textRevision;
    const excluded = [...this.claimedIds].filter((id) => !consumedIds.includes(id));
    this.resetSession(excluded);
    this.patch({ ...(unchanged ? { note: '', revision: this.snapshot.revision + 1 } : {}),
      resolution: saved && !unchanged ? 'saved' : 'none', saveError: false, discardError: false });
    this.flushWords();
  }
  chooseSuccessor() {
    if (!this.isCurrent() || this.snapshot.operation || this.snapshot.resolution !== 'saved') return false;
    this.patch({ resolution: 'none' }); return true;
  }
  useRecordingWords(text: string, queueId: string) {
    if (!this.isCurrent() || this.snapshot.operation || this.snapshot.loading !== 'ready' || !this.excludedIds.has(queueId)) return;
    this.excludedIds.delete(queueId); this.receivedIds.delete(queueId); this.receive(text, queueId);
  }
}

/** Observer only: Settings success must never be changed by a draft observer. */
export function settlePrayerUpdateDraft(settlement: Settlement): void {
  if (settlement.reviewed.intent.kind !== 'append') return;
  try { drafts.get(keyFor(settlement.auth, settlement.reviewed.prayerId))?.settle(settlement); } catch { /* retain words; reconcile on reopen */ }
}
export function prayerUpdateDraft(auth: ContentLifecycleAuthExpectation, prayerId: string, initialNote = '') {
  if (!prayerEditorIsCurrent(auth)) return new PrayerUpdateDraft(auth, prayerId, initialNote);
  retireStaleRecords();
  const key = keyFor(auth, prayerId);
  let draft = drafts.get(key);
  if (!draft) { draft = new PrayerUpdateDraft(auth, prayerId, initialNote); drafts.set(key, draft); }
  watchRecords(); void draft.refreshAvailability();
  return draft;
}
